import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    AUDIT_API_BASE_URL_TOKEN,
    AuditEntityTypeCatalogConfig,
    AuditLog,
    SearchCriteria,
} from '@raptor-fe/core';

/**
 * HTTP client for the isolated audit microservice (Spec #37950, BE-S8 / BE-S9).
 * Uses <c>AUDIT_API_BASE_URL_TOKEN</c> rather than the main API base URL because
 * the audit microservice deploys independently and its endpoints live on a
 * separate origin (AC-19).
 */
@Injectable({ providedIn: 'root' })
export class AuditLogService {
    private readonly http = inject(HttpClient);
    private readonly auditApiBaseUrl = inject(AUDIT_API_BASE_URL_TOKEN);

    /**
     * Tenant-admin search. The server enforces tenant + branch scoping based
     * on the URL segments and clamps the page size at 100.
     */
    search(tenantId: string, branchId: string, criteria: SearchCriteria): Observable<AuditLog[]> {
        return this.http.post<AuditLog[]>(
            `${this.auditApiBaseUrl}v1/tenant/${tenantId}/branch/${branchId}/auditlog/search`,
            criteria,
        );
    }

    /**
     * Tenant-admin entity-type picker. Returns only catalog rows where
     * <c>showInSearchCriteria == true</c> (AC-28).
     */
    getEntityTypes(tenantId: string, branchId: string): Observable<AuditEntityTypeCatalogConfig[]> {
        return this.http.get<AuditEntityTypeCatalogConfig[]>(
            `${this.auditApiBaseUrl}v1/tenant/${tenantId}/branch/${branchId}/auditlog/entity-types`,
        );
    }

    /**
     * Admin-only: every concrete BaseEntity descendant the audit microservice
     * has discovered, with the PascalCase-split default label.
     */
    getDiscoveredTypes(): Observable<AuditEntityTypeCatalogConfig[]> {
        return this.http.get<AuditEntityTypeCatalogConfig[]>(
            `${this.auditApiBaseUrl}v1/auditlog/admin/catalog/discovered-types`,
        );
    }

    /**
     * Admin-only: full catalog (every discovered type with its persisted
     * configuration row joined in).
     */
    getAdminCatalog(): Observable<AuditEntityTypeCatalogConfig[]> {
        return this.http.get<AuditEntityTypeCatalogConfig[]>(
            `${this.auditApiBaseUrl}v1/auditlog/admin/catalog`,
        );
    }

    /**
     * Admin-only: persists changes to the catalog (DisplayLabel and
     * ShowInSearchCriteria are the only mutable fields). Updates touching
     * unknown TypeNames are rejected by the server with 400.
     */
    saveAdminCatalog(updates: AuditEntityTypeCatalogConfig[]): Observable<AuditEntityTypeCatalogConfig[]> {
        return this.http.put<AuditEntityTypeCatalogConfig[]>(
            `${this.auditApiBaseUrl}v1/auditlog/admin/catalog`,
            updates,
        );
    }
}
